// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Landbridge — colour system + WCAG contrast helper.
//
// The play surface is a flat vector world: land plates on a deep slate sea. Four
// roles carry MEANING and must be visible AND told apart without colour alone
// (principle #22 + colour-blind safety): the START (green), the DESTINATION
// (amber), the CHAIN you have built (teal), and the current TAIL (bright teal).
// Each is also marked with a glyph (start ▶, dest ⚑) and the guess-list text, so
// hue is never the only cue. Every meaningful colour is pinned by contrast.test.ts.

export const PALETTE = {
  // map
  sea: '#0c1c28',
  seaDeep: '#081521',
  land: '#2a3a48', // country not in the chain
  landBorder: '#547083', // the stroke delineating land from sea + neighbours

  // panels / chrome
  panel: '#12293a',
  panelDeep: '#0e2130',

  // text
  text: '#e8f0f4',
  textMuted: '#a9bcc8',
  ink: '#0c1c28', // dark glyph drawn ON a bright fill

  // roles on the map
  start: '#46cf72', // start country
  chain: '#35c9bd', // a country in the built chain
  tail: '#8bf3e6', // the current tail (brightest)
  dest: '#f4b83f', // destination country
  route: '#ffd166', // the optimal-route reveal line
  misfire: '#ff6b64', // a wrong-hop flash / low-lives warning

  // feedback
  good: '#46cf72',
  danger: '#ff6b64',
} as const;

/* ── WCAG contrast ────────────────────────────────────────────────────────── */

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Relative luminance of a #rrggbb colour (WCAG 2.1). */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) throw new Error(`not a hex colour: ${hex}`);
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => channel(parseInt(h, 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #rrggbb colours (1..21, symmetric). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
